import React, { useEffect, useState } from 'react';
import {
  ArrowUpRight,
  Clock3,
  FileSpreadsheet,
  MessageCircle,
  Send,
  Sparkles,
  UserRound,
  UploadCloud,
} from 'lucide-react';
import Button from '../components/ui/Button';
import MarkdownRenderer from '../components/ui/MarkdownRenderer';
import { answerQuestionFromDataset, getStoredDataset } from '../services/analytics';
import { askGeminiFlashAboutDataset } from '../services/gemini';
import {
  getBillingSummary,
  getSubscriptionErrorMessage,
  recordUsage,
  type BillingSummary,
} from '../services/subscription';

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  timestamp: string;
};

const suggestedQuestions = [
  'What changed this month?',
  'Which area performed best?',
  'Summarize this file',
];

const AskGabMicPage: React.FC = () => {
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [billingSummary, setBillingSummary] = useState<BillingSummary | null>(null);
  const dataset = getStoredDataset();

  useEffect(() => {
    let isMounted = true;

    getBillingSummary()
      .then((summary) => {
        if (isMounted) setBillingSummary(summary);
      })
      .catch(() => {
        if (isMounted) setBillingSummary(null);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '1rem 4.4rem 1rem 1.25rem',
    background: 'transparent',
    border: 0,
    color: 'var(--text-main)',
    fontSize: '1rem',
    outline: 'none',
    resize: 'vertical',
    minHeight: '58px',
    maxHeight: '140px',
  };

  const handleAskAI = async (forcedQuestion?: string) => {
    const question = (forcedQuestion ?? query).trim();
    if (!question || isLoading) return;

    const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const userMessage: ChatMessage = {
      id: `${Date.now()}-user`,
      role: 'user',
      text: question,
      timestamp,
    };

    setIsLoading(true);
    setQuery('');
    setMessages((currentMessages) => [...currentMessages, userMessage]);

    try {
      if (dataset) {
        const usage = await recordUsage('aiQuestion');
        setBillingSummary((current) => current ? { ...current, usage } : current);
      }

      const answer = await askGeminiFlashAboutDataset(question, dataset);
      setMessages((currentMessages) => [
        ...currentMessages,
        {
          id: `${Date.now()}-assistant`,
          role: 'assistant',
          text: answer,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
      ]);
    } catch (error) {
      const isUsageError = error instanceof Error && /limit|resource-exhausted|unauthenticated/i.test(error.message);
      const fallback = isUsageError ? '' : answerQuestionFromDataset(question, dataset);
      const message = getSubscriptionErrorMessage(error);
      setMessages((currentMessages) => [
        ...currentMessages,
        {
          id: `${Date.now()}-assistant`,
          role: 'assistant',
          text: fallback ? `${message}\n\nLocal data answer:\n${fallback}` : message,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="page-stack">
      <header className="page-header">
        <div>
          <h1 style={{ fontSize: '2.2rem', marginBottom: '0.55rem' }}>Ask your data</h1>
          <p className="page-kicker">Get clear answers based only on the file you uploaded.</p>
          {dataset && (
            <span className="file-chip" style={{ marginTop: '1rem' }}>
              <FileSpreadsheet size={17} color="var(--gabmic-orange)" />
              {dataset.fileName}
              <span className="status-dot" />
            </span>
          )}
        </div>
      </header>

      {!dataset && (
        <div className="premium-card" style={{ padding: '1rem', display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--text-secondary)' }}>Upload CSV or Excel data before asking analysis questions.</span>
          <a href="/upload"><Button variant="primary" size="sm"><UploadCloud size={16} />Upload data</Button></a>
        </div>
      )}

      {dataset && (
        <div className="premium-card" style={{ padding: '1rem', display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--text-secondary)' }}>
            {billingSummary
              ? `${billingSummary.customer.planName} plan: ${formatRemainingQuestions(billingSummary.usage.remaining.aiQuestions)} remaining.`
              : 'Sign in to enforce your Ask GabMic monthly allowance.'}
          </span>
          <a href="/pricing">
            <Button variant={billingSummary?.customer.effectivePlanId === 'free' ? 'primary' : 'outline'} size="sm">View pricing</Button>
          </a>
        </div>
      )}

      <div className="ask-layout">
        <main style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {suggestedQuestions.map((question) => (
              <button
                key={question}
                type="button"
                className="soft-chip"
                onClick={() => handleAskAI(question)}
                disabled={isLoading}
                style={{ cursor: 'pointer' }}
              >
                <MessageCircle size={16} />
                {question}
              </button>
            ))}
          </div>

          <div style={{ minHeight: '410px', display: 'flex', flexDirection: 'column', gap: '1rem', padding: '0.25rem 0' }}>
            {messages.length === 0 && !isLoading && (
              <div className="premium-card" style={{ padding: '2rem', maxWidth: '620px' }}>
                <span className="icon-tile" style={{ marginBottom: '1rem' }}>
                  <Sparkles size={20} />
                </span>
                <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>Ready when your data is ready</h3>
                <p style={{ margin: 0, fontSize: '0.95rem' }}>Ask about totals, changes, top categories, summaries, or unusual patterns in your uploaded file.</p>
              </div>
            )}

            {messages.map((message) => (
              <div
                key={message.id}
                style={{
                  display: 'flex',
                  justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                  gap: '0.75rem',
                  alignItems: 'flex-start',
                }}
              >
                {message.role === 'assistant' && (
                  <span className="icon-tile" style={{ flex: '0 0 auto', width: '34px', height: '34px' }}>
                    <Sparkles size={17} />
                  </span>
                )}
                <div
                  className="premium-card"
                  style={{
                    padding: '1rem 1.1rem',
                    maxWidth: message.role === 'user' ? '420px' : '680px',
                    background: message.role === 'user'
                      ? 'linear-gradient(145deg, rgba(255,255,255,0.07), rgba(255,255,255,0.02)), var(--bg-surface-raised)'
                      : undefined,
                  }}
                >
                  {message.role === 'assistant' ? (
                    <MarkdownRenderer content={message.text} />
                  ) : (
                    <p style={{ color: 'var(--text-main)', margin: 0, fontSize: '0.98rem', lineHeight: 1.72, whiteSpace: 'pre-wrap' }}>{message.text}</p>
                  )}
                  <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.78rem', marginTop: '0.7rem' }}>{message.timestamp}</span>
                </div>
                {message.role === 'user' && (
                  <span className="icon-tile" style={{ flex: '0 0 auto', width: '34px', height: '34px', color: 'var(--text-secondary)' }}>
                    <UserRound size={17} />
                  </span>
                )}
              </div>
            ))}

            {isLoading && (
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <span className="icon-tile" style={{ width: '34px', height: '34px' }}><Sparkles size={17} /></span>
                <div className="premium-card" style={{ padding: '1rem 1.1rem', color: 'var(--text-secondary)' }}>
                  Analyzing the uploaded data...
                </div>
              </div>
            )}
          </div>

          <div className="premium-card" style={{ position: 'sticky', bottom: '1rem', padding: '0.35rem', display: 'flex', alignItems: 'flex-end', gap: '0.75rem' }}>
            <textarea
              style={inputStyle}
              rows={1}
              placeholder="Ask a question about your data..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleAskAI();
                }
              }}
            />
            <Button
              variant="primary"
              onClick={() => handleAskAI()}
              disabled={!query.trim() || isLoading}
              style={{ borderRadius: '999px', width: '52px', height: '52px', padding: 0, flex: '0 0 auto' }}
              aria-label="Ask GabMic"
            >
              <Send size={20} />
            </Button>
          </div>
          <p style={{ margin: '0 auto', fontSize: '0.85rem', color: 'var(--text-muted)' }}>GabMic can make mistakes. Check important results.</p>
        </main>

        <aside className="premium-card desktop-only" style={{ padding: '1.25rem', minHeight: '520px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '1.2rem' }}>
            <Clock3 size={18} color="var(--text-secondary)" />
            <strong>Question history</strong>
          </div>
          {messages.filter((message) => message.role === 'user').length === 0 ? (
            <p style={{ margin: 0, fontSize: '0.9rem' }}>Your questions in this session will appear here.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {messages.filter((message) => message.role === 'user').slice(-6).reverse().map((message) => (
                <button
                  key={message.id}
                  type="button"
                  onClick={() => setQuery(message.text)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '0.75rem',
                    padding: '1rem 0',
                    border: 0,
                    borderBottom: '1px solid var(--border-color)',
                    background: 'transparent',
                    color: 'var(--text-main)',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <span>
                    <span style={{ display: 'block', fontSize: '0.9rem' }}>{message.text}</span>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>{message.timestamp}</span>
                  </span>
                  <ArrowUpRight size={16} color="var(--text-secondary)" />
                </button>
              ))}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

const formatRemainingQuestions = (remaining: number | null) => {
  if (remaining === null) return 'custom AI questions';
  return `${remaining.toLocaleString()} Ask GabMic question${remaining === 1 ? '' : 's'}`;
};

export default AskGabMicPage;
